import { type FC } from "react";

import { Mail } from "lucide-react";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Separator } from "@/shared/components/ui/separator";
import { RELATIONSHIP_OPTIONS } from "@/shared/constants/relationshipOptions";
import { useUser } from "@/shared/hooks/useAppContext";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { usePersonInvitations } from "@/shared/hooks/useEstateInvitations";
import { useInviteeForm } from "@/shared/hooks/useInviteeForm";
import { useWillStepAnswers } from "@/shared/hooks/useWillStepAnswers";
import { validateInviteForm } from "@/shared/utils/validators";

import { LegalAuthorityStepLayout } from "./LegalAuthorityStepLayout";

interface LegalAuthorityExecutorInviteStepProps {
  personFirstName: string;
  estateId: number;
  executorName: string;
  onBack: () => void;
  onContinue: () => void;
  onSkip: () => void;
}

export const LegalAuthorityExecutorInviteStep: FC<LegalAuthorityExecutorInviteStepProps> = ({
  personFirstName,
  estateId,
  executorName,
  onBack,
  onContinue,
  onSkip,
}) => {
  const personName = personFirstName || "your loved one";
  const executorFirstName = executorName.split(/\s+/)[0] || "them";
  const user = useUser();
  const { fullName: decedentFullName } = useDeceasedUserData();
  const { sendInvitation, isSendingInvitation } = usePersonInvitations(estateId);
  const { saveAnswers } = useWillStepAnswers(estateId);

  const inviterName = user?.first_name || "Someone";
  const estateName = decedentFullName || `${personName}'s`;

  const {
    contactId,
    pickContact,
    fullName,
    setFullName,
    relationship,
    setRelationship,
    email,
    setEmail,
    phone,
    setPhone,
    errors,
    setErrors,
    saveContact,
  } = useInviteeForm(executorName);

  const handleSendInvite = async () => {
    const next = validateInviteForm(fullName, email);
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    try {
      await saveContact();

      await sendInvitation.mutateAsync({
        estateId,
        email: email.trim().toLowerCase(),
        role: "member",
      });

      await saveAnswers({
        "executor-invite": {
          name: fullName.trim(),
          email: email.trim().toLowerCase(),
          relationship: relationship || null,
          phone: phone.trim() || null,
        },
      });

      onContinue();
    } catch {
      // hooks handle snackbar errors
    }
  };

  return (
    <LegalAuthorityStepLayout
      title={`Invite ${executorFirstName} to ${personName}'s estate`}
      description={`${personName}'s will names ${executorFirstName} to handle the estate, so bringing ${executorFirstName} in is the next step. It's common for families to move things forward as a team, and thanks to you, the hardest part is already underway. None of your work resets. It's all saved in the account.`}
      activeStep={2}
      onBack={onBack}
      actionButton={
        <div className="flex items-center gap-4">
          <button onClick={onSkip} className="text-sm font-medium hover:text-secondary">
            Skip for now
          </button>
          <Button
            variant="brand"
            size="md"
            onClick={handleSendInvite}
            disabled={isSendingInvitation}
          >
            Send invite
          </Button>
        </div>
      }
    >
      <Separator />

      <PeopleSelector
        mode="single"
        label="Choose from people"
        placeholder="Choose someone already in People"
        value={contactId}
        onChange={pickContact}
      />

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Full name</Label>
          <Input
            value={fullName}
            onChange={(e) => {
              setFullName(e.target.value);
              setErrors((prev) => ({ ...prev, fullName: undefined }));
            }}
            placeholder="Enter full name"
            className={errors.fullName ? "border-error" : ""}
          />
          <FieldError message={errors.fullName} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Relationship to {personFirstName || "decedent"}</Label>
          <Select value={relationship} onValueChange={setRelationship}>
            <SelectTrigger>
              <SelectValue placeholder="Select a relationship" />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>Email</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            placeholder="example@email.com"
            className={errors.email ? "border-error" : ""}
          />
          <FieldError message={errors.email} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label>
            Phone <span className="text-tertiary">(optional)</span>
          </Label>
          <PhoneNumberInput value={phone} onChange={setPhone} />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-primary">
        <div className="flex items-center gap-2 bg-subtle px-4 py-3">
          <Mail className="h-4 w-4 text-secondary" aria-hidden="true" />
          <p className="text-sm text-secondary">Here's the invitation we'll send</p>
        </div>
        <Separator />
        <div className="flex flex-col gap-3 bg-white p-4 text-sm">
          <p>
            {inviterName} invited you to {estateName} estate on Elayne.
          </p>
          <p>
            Elayne is an estate settlement platform built for families and attorneys, supporting
            them from first steps, to probate, to finding and transferring the estate's assets.
          </p>
          <p>
            {personName}'s will names you to handle the estate, so what comes next starts with you.
            The account is already set up, and documents and details are in place.
          </p>
          <p className="font-medium text-link underline">Join {estateName} estate</p>
        </div>
      </div>
    </LegalAuthorityStepLayout>
  );
};
